import React, { useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  CircularProgress,
} from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  PIPELINE_COLUMNS,
  flattenBrandReports,
  groupTestsByPipelineColumn,
  getStatusForColumn,
} from '../../utils/pipelineUtils';
import { BRAND } from '../../theme/brandTheme';

/**
 * Kanban pipeline — drag cards across Taslak / Canlı / outcome columns
 */
const TestPipeline = ({
  reports,
  canDrag,
  onStatusChange,
  onReportDetail,
  loadingReportId,
  apiLoading,
}) => {
  const [draggingId, setDraggingId] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);

  const flatTests = useMemo(() => flattenBrandReports(reports), [reports]);
  const grouped = useMemo(() => groupTestsByPipelineColumn(flatTests), [flatTests]);

  const handleDragStart = (e, testId) => {
    if (!canDrag) return;
    e.dataTransfer.setData('text/test-id', testId);
    e.dataTransfer.effectAllowed = 'move';
    setDraggingId(testId);
  };

  const handleDragEnd = () => {
    setDraggingId(null);
    setDropTarget(null);
  };

  const handleDragOver = (e, columnId) => {
    if (!canDrag) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropTarget(columnId);
  };

  const handleDrop = (e, columnId) => {
    e.preventDefault();
    setDropTarget(null);
    setDraggingId(null);
    if (!canDrag) return;

    const testId = e.dataTransfer.getData('text/test-id');
    const test = flatTests.find((t) => t.id === testId);
    if (!test) return;

    const newStatus = getStatusForColumn(columnId);
    if (test.status !== newStatus) {
      onStatusChange(test.id, newStatus);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        overflowX: 'auto',
        pb: 2,
        minHeight: 420,
      }}
    >
      {PIPELINE_COLUMNS.map((column) => {
        const cards = grouped[column.id] || [];
        const isTarget = dropTarget === column.id;

        return (
          <Paper
            key={column.id}
            elevation={0}
            onDragOver={(e) => handleDragOver(e, column.id)}
            onDragLeave={() => setDropTarget(null)}
            onDrop={(e) => handleDrop(e, column.id)}
            sx={{
              flex: '0 0 240px',
              minWidth: 240,
              bgcolor: isTarget ? 'rgba(37, 12, 75, 0.04)' : '#f7f9fc',
              border: isTarget ? `2px dashed ${BRAND.purple}` : `1px solid ${BRAND.borderLight}`,
              borderRadius: 2,
              display: 'flex',
              flexDirection: 'column',
              maxHeight: 640,
            }}
          >
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderBottom: '1px solid #eee',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                bgcolor: '#fff',
                borderRadius: '8px 8px 0 0',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: column.color,
                  }}
                />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: BRAND.purple }}>
                  {column.label}
                </Typography>
              </Box>
              <Chip label={cards.length} size="small" sx={{ height: 22, fontWeight: 600 }} />
            </Box>

            <Box sx={{ p: 1.5, flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}>
              {cards.length === 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ p: 1, textAlign: 'center' }}>
                  {canDrag ? 'Kartları buraya sürükleyin' : 'Boş'}
                </Typography>
              )}

              {cards.map((test) => {
                const isLoading = apiLoading[test.id] || loadingReportId === test.id;
                const isDragging = draggingId === test.id;

                return (
                  <Paper
                    key={test.id}
                    draggable={canDrag && !isLoading}
                    onDragStart={(e) => handleDragStart(e, test.id)}
                    onDragEnd={handleDragEnd}
                    elevation={0}
                    sx={{
                      p: 1.5,
                      cursor: canDrag ? 'grab' : 'pointer',
                      border: '1px solid #eee',
                      borderRadius: 1.5,
                      bgcolor: '#fff',
                      opacity: isDragging ? 0.5 : 1,
                      transition: 'box-shadow 0.15s',
                      '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
                    }}
                    onClick={() => {
                      if (!isLoading) onReportDetail(test);
                    }}
                  >
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start' }}>
                      {canDrag && (
                        <DragIndicatorIcon sx={{ fontSize: 16, color: '#bbb', mt: 0.25 }} />
                      )}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: BRAND.purple,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {test.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {test.brandName}
                        </Typography>
                        {test.hasSignificant && (test.resultStatuses || []).includes('Kazandı') && (
                          <Chip label="Anlamlı" size="small" color="success" sx={{ mt: 0.75, height: 20 }} />
                        )}
                      </Box>
                      {isLoading && <CircularProgress size={16} color="primary" />}
                    </Box>
                  </Paper>
                );
              })}
            </Box>
          </Paper>
        );
      })}
    </Box>
  );
};

export default TestPipeline;
